'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { staggerContainer } from '@/lib/animations';
import type { Testimonial } from '@/lib/content-schema';

interface Props {
  testimonials: Testimonial[];
}

export function SocialProof({ testimonials }: Props) {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <section className="py-24 md:py-32 bg-zibarit-negro">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Stats */}
        <motion.div
          ref={ref}
          variants={staggerContainer}
          initial="initial"
          animate={isInView ? 'animate' : 'initial'}
          className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-8 mb-20"
        >
        </motion.div>

        {/* Testimonials carousel */}
        <div className="max-w-3xl mx-auto text-center">
          <div className="relative min-h-[200px]">
            <AnimatePresence mode="wait">
              <motion.blockquote
                key={currentTestimonial}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.5 }}
                className="absolute inset-0"
              >
                <p className="text-title text-xl md:text-2xl text-zibarit-bone">
                  &ldquo;{testimonials[currentTestimonial].quote}&rdquo;
                </p>
              </motion.blockquote>
            </AnimatePresence>
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-8">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentTestimonial(index)}
                className={`w-2 h-2 rounded-full transition-colors ${
                  index === currentTestimonial
                    ? 'bg-zibarit-verde-oscuro'
                    : 'bg-zibarit-marron-secundario/30 hover:bg-zibarit-marron-secundario/50'
                }`}
                aria-label={`Ver testimonio ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
